import { useEffect, useCallback } from "react";
import notificationApi from "@/api/notification";
import { useAppSelector, useAppDispatch } from "@/hooks/types";
import { addNotification } from "@/store/app/slice";
import { getIsCreditsEnabled } from "@/store/tenant/selectors";
import { getUserCreditBalance, getUserDetails } from "@/store/user/selectors";
import {
    NotificationDetails,
    NotificationMessageType,
    NotificationType,
} from "@/types/app";
import { INotification } from "@/types/notification";
import { trackPostHogEvent } from "@/utils/tracking";

type DiscountType = "abandoned_cart" | "low_balance";

const PROMO_CODES = {
    abandoned_cart: "INTRO50",
    low_balance: "SAVE50",
};

const getNotificationConfig = (
    type: DiscountType,
    promoCode: string,
): NotificationDetails => {
    switch (type) {
        case "abandoned_cart": {
            return {
                title: "Don't miss out!",
                desc: [
                    "50% off your next Pro or Ultimate credit pack",
                    `Use code ${promoCode} at checkout.`,
                ],
                messageType: NotificationMessageType.DISCOUNT_ABANDONED_CART,
                type: NotificationType.PROMOTION,
                static: true,
            };
        }
        case "low_balance": {
            return {
                title: "Need more credits?",
                desc: `Get 50% off your next purchase with code ${promoCode}`,
                messageType: NotificationMessageType.DISCOUNT_LOW_BALANCE,
                type: NotificationType.PROMOTION,
                static: true,
            };
        }
    }
};

export const useNotifications = (
    pendingNotifications: INotification[] = [],
) => {
    const dispatch = useAppDispatch();
    const userDetails = useAppSelector(getUserDetails);
    const areCreditsEnabled = useAppSelector(getIsCreditsEnabled);
    const creditBalance = useAppSelector(getUserCreditBalance);

    const processNotification = useCallback(
        async (notification: INotification) => {
            try {
                const notificationConfig = getNotificationConfig(
                    notification.type,
                    PROMO_CODES[notification.type as DiscountType],
                );
                dispatch(addNotification(notificationConfig));

                // Mark as shown
                await notificationApi.markNotificationShown(notification._id);

                trackPostHogEvent("notification", {
                    notification_type: notification.type,
                    event: "notification_shown",
                });
            } catch (error) {
                console.error("Error processing notification:", error);
            }
        },
        [dispatch],
    );

    const addLowBalanceNotification = useCallback(async () => {
        await notificationApi.createNotification({
            type: "low_balance",
            expiresAt: new Date(Date.now() + 1000 * 60 * 5), // 5 minutes
            sentAt: new Date(),
        });
    }, []);

    useEffect(() => {
        // Only trigger if user data is loaded and credits are enabled
        if (creditBalance === 0 && areCreditsEnabled && userDetails?.id) {
            addLowBalanceNotification();
        }
    }, [
        addLowBalanceNotification,
        creditBalance,
        areCreditsEnabled,
        userDetails?.id,
    ]);

    // Process notifications when they change
    useEffect(() => {
        if (
            !areCreditsEnabled ||
            !userDetails?.id ||
            !pendingNotifications.length
        ) {
            return;
        }

        // TODO: Handle multiple notifications - for now just show the first one
        // Future: Process each notification with delays between them
        const firstNotification = pendingNotifications[0];

        setTimeout(() => {
            processNotification(firstNotification);
        }, 3000); // 3 seconds delay before showing
    }, [
        pendingNotifications,
        userDetails?.id,
        areCreditsEnabled,
        processNotification,
    ]);

    return null;
};
